import React from 'react';
import { HelpCircle, MessageCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Card, CardContent } from '../../components/ui/card';
import Navbar from '../../components/Navbar';
import { useLanguage } from '../../contexts/LanguageContext';

const FAQ = () => {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const faqs = [
    {
      category: tr ? 'Genel Bilgiler' : 'General Information',
      questions: [
        {
          q: tr ? 'SummitFlow ne zaman ve nerede düzenleniyor?' : 'When and where does SummitFlow take place?',
          a: tr
            ? 'SummitFlow — Küresel Liderlik ve İnovasyon Zirvesi 14–16 Ekim 2026 tarihlerinde İstanbul, Türkiye\'de düzenlenecektir.'
            : 'SummitFlow — the Global Leadership & Innovation Summit — takes place 14–16 October 2026 in Istanbul, Türkiye.',
        },
        {
          q: tr ? 'Zirveye kimler katılabilir?' : 'Who can attend the summit?',
          a: tr
            ? 'Zirve; akredite delegelere, iş dünyası temsilcilerine, medya profesyonellerine ve genel katılımcılara açıktır. Tüm katılımcılar ilgili kanaldan kayıt olur ve onay alır.'
            : 'The summit is open to accredited delegates, business representatives, media professionals and general attendees. All participants register and receive approval via the relevant channel.',
        },
        {
          q: tr ? 'Nasıl kayıt olabilirim?' : 'How do I register?',
          a: tr
            ? 'Kayıt, ana sayfadaki "Katılım Yolları" bölümünden başlar. Rolünüze göre Blue Zone (delege), Green Zone (katılımcı), Tedarikçi veya Yüklenici akışını seçebilirsiniz.'
            : 'Registration begins from the "Ways to Participate" section on the home page. Choose the Blue Zone (delegate), Green Zone (attendee), Supplier or Contractor journey based on your role.',
        },
        {
          q: tr ? 'Zirvede hangi diller kullanılacak?' : 'What languages will be used?',
          a: tr
            ? 'Ana oturumlar İngilizce ve Türkçe olarak, seçili oturumlarda simültane çeviri ile yürütülecektir.'
            : 'Main sessions run in English and Turkish, with simultaneous interpretation in selected sessions.',
        },
      ],
    },
    {
      category: tr ? 'Katılım ve Erişim' : 'Participation & Access',
      questions: [
        {
          q: tr ? 'Blue Zone ile Green Zone arasındaki fark nedir?' : 'What is the difference between the Blue Zone and Green Zone?',
          a: tr
            ? 'Blue Zone, akredite delegeler için resmi oturumların ve müzakerelerin yapıldığı alandır. Green Zone ise iş dünyası, medya ve genel katılımcılara açık, panel ve sergileri içeren programdır.'
            : 'The Blue Zone hosts official sessions for accredited delegates. The Green Zone is open to business, media and general attendees, featuring panels and exhibitions.',
        },
        {
          q: tr ? 'Yan etkinliklere ve sergilere katılabilir miyim?' : 'Can I attend side events and exhibitions?',
          a: tr
            ? 'Evet. Zirve boyunca çok sayıda yan etkinlik, sergi ve stand yer alacaktır. Erişim, akreditasyon seviyenize bağlıdır; Green Zone etkinlikleri katılımcılara açıktır.'
            : 'Yes. The summit hosts numerous side events, exhibitions and stands. Access depends on your accreditation level; Green Zone events are open to attendees.',
        },
        {
          q: tr ? 'Tedarikçi veya yüklenici olarak nasıl katılırım?' : 'How can I participate as a supplier or contractor?',
          a: tr
            ? 'Tedarikçiler B2B kataloğa ürün yükleyip teklif alır; yükleniciler stand tasarım ihalelerine teklif verir. Her iki kayıt da ana sayfadaki ilgili kartlardan başlar.'
            : 'Suppliers list products in the B2B catalogue and receive RFQs; contractors bid on stand-design tenders. Both journeys begin from the cards on the home page.',
        },
        {
          q: tr ? 'Konaklama desteği var mı?' : 'Is there accommodation support?',
          a: tr
            ? 'Evet. Organizasyon, İstanbul\'daki otellerle farklı bütçelere uygun konaklama seçenekleri koordine etmektedir. Detaylar kayıt sonrası paylaşılır.'
            : 'Yes. The organiser coordinates accommodation options at various price points with hotels in Istanbul. Details are shared after registration.',
        },
      ],
    },
    {
      category: tr ? 'Lojistik ve Güvenlik' : 'Logistics & Safety',
      questions: [
        {
          q: tr ? 'Katılım ücretli midir?' : 'Is participation paid?',
          a: tr
            ? 'Delege kaydı ücretsizdir. Tedarikçi ve yüklenici katılımı, etkinlik protokolü ve kategori üyeliği kurallarına tabidir.'
            : 'Delegate registration is free. Supplier and contractor participation is subject to the event protocol and category membership rules.',
        },
        {
          q: tr ? 'Engelli katılımcılar için erişilebilirlik hizmetleri var mı?' : 'Are there accessibility services for participants with disabilities?',
          a: tr
            ? 'Evet. Mekân; tekerlekli sandalye erişimi, erişilebilir tuvaletler ve destek hizmetleriyle tam erişilebilirlik sağlar.'
            : 'Yes. The venue provides full accessibility with wheelchair access, accessible restrooms and assistance services.',
        },
        {
          q: tr ? 'Mekâna ulaşım nasıl sağlanır?' : 'How do I get to the venue?',
          a: tr
            ? 'Mekâna toplu taşıma, servis ve taksi ile kolayca ulaşılabilir. Ayrıntılı ulaşım bilgisi kayıt sonrası paylaşılır.'
            : 'The venue is easily reached by public transport, shuttle and taxi. Detailed transport information is shared after registration.',
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EAF1F5] via-white to-[#EAF7F0]">
      <Navbar scrolled={scrolled} />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.pexels.com/photos/9275222/pexels-photo-9275222.jpeg"
            alt="Summit"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A3A5A]/85 via-[#0A3A5A]/75 to-[#216E4E]/80"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HelpCircle className="h-20 w-20 text-white mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-playfair font-medium tracking-tight text-white mb-6">
            {tr ? 'Sık Sorulan Sorular' : 'Frequently Asked Questions'}
          </h1>
          <p className="text-xl md:text-2xl text-white/95 leading-relaxed">
            {tr ? 'SummitFlow hakkında sık sorulan soruların yanıtları' : 'Answers to common questions about SummitFlow'}
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqs.map((category, index) => (
            <div key={index} className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <MessageCircle className="h-8 w-8 text-[#216E4E]" />
                <h2 className="text-3xl md:text-4xl font-playfair font-medium tracking-tight text-gray-900">{category.category}</h2>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                {category.questions.map((faq, idx) => (
                  <AccordionItem key={idx} value={`faq-${index}-${idx}`} className="border-0 bg-white shadow-sm rounded-2xl overflow-hidden" data-testid={`faq-page-item-${index}-${idx}`}>
                    <AccordionTrigger className="px-8 py-6 hover:bg-[#EAF7F0] transition-colors text-left">
                      <span className="text-lg font-semibold text-gray-900">{faq.q}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-8 pb-6">
                      <p className="text-gray-700 leading-relaxed text-base">{faq.a}</p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}

          {/* Contact Card */}
          <Card className="border-0 shadow-lg rounded-3xl bg-gradient-to-br from-[#0A3A5A] to-[#216E4E] text-white mt-16">
            <CardContent className="p-12 text-center">
              <MessageCircle className="h-16 w-16 mx-auto mb-6" />
              <h3 className="text-3xl font-playfair font-medium tracking-tight mb-4">
                {tr ? 'Hâlâ sorunuz mu var?' : 'Still have questions?'}
              </h3>
              <p className="text-xl mb-8 text-white/90">
                {tr ? 'Destek ekibimiz SummitFlow ile ilgili tüm sorularınızda yardımcı olmaya hazır.' : 'Our support team is here to help with any inquiries about SummitFlow.'}
              </p>
              <a href="mailto:hello@summitflow.net" className="inline-block bg-white text-[#0A3A5A] px-8 py-4 rounded-full font-semibold text-lg hover:bg-[#EAF7F0] transition-colors" data-testid="faq-contact-support">
                {tr ? 'Destek ile İletişim' : 'Contact Support'}
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
